import { indexByFn } from 'volta/utils/array-utils';
import { getColor, SemanticColors } from 'volta/utils/style-utils';

import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import { baseDataZoom, EChartsOption } from 'volta/components/bv-chart';
import type { ITransactionAgg } from 'volta/models/types/transaction';
import type IntlService from 'ember-intl/services/intl';
import type AppNavState from 'volta/services/app-nav-state';

type TSeriesKey = 'incoming' | 'outgoing' | 'onHand';

const SeriesColors = (theme: 'light' | 'dark') => ({
  incoming: getColor('orange')!,
  outgoing: getColor('blue')!,
  onHand: SemanticColors.bvText(theme)
});

interface IArgs {
  transactions: ITransactionAgg[];
}

export default class TransactionsChart extends Component<IArgs> {
  @service intl!: IntlService;
  @service appNavState!: AppNavState;

  get transactions() {
    return this.args.transactions ?? [];
  }

  get dates() {
    return this.transactions.map((tx) => new Date(tx.transactionDate).getTime()).sort();
  }

  @cached
  get data(): Record<TSeriesKey, [string | number, number][]> {
    const { dates } = this;
    const dict = indexByFn(this.transactions, (tx) => new Date(tx.transactionDate).getTime());
    return dates.reduce(
      (acc, date) => {
        ['incoming', 'outgoing', 'onHand'].forEach((k: TSeriesKey) => {
          const value = dict[date][k];
          acc[k].push([date, Math.abs(value)]);
        });

        return acc;
      },
      { incoming: [], outgoing: [], onHand: [] } as Record<TSeriesKey, [string | number, number][]>
    );
  }

  get chartOptions(): EChartsOption {
    const { data, dates } = this;
    const colors = SeriesColors(this.appNavState.appliedTheme);
    const series: EChartsOption[] = ['incoming', 'outgoing']
      .map((k: TSeriesKey) => {
        return {
          name: this.intl.t(k),
          data: data[k],
          type: 'bar',
          showSymbol: false,
          color: colors[k]
        };
      })
      .concat(
        ['onHand'].map((k: TSeriesKey) => {
          return {
            name: this.intl.t(k),
            data: data[k],
            type: 'line',
            showSymbol: false,
            color: colors[k],
            lineStyle: {
              type: [4, 4]
            }
          };
        })
      );

    return {
      legend: {
        selected: {
          [this.intl.t('onHand')]: false
        },
        data: Object.keys(data).map((k) => this.intl.t(k))
      },
      xAxis: {
        data: dates
      },
      dataZoom: baseDataZoom(),
      series
    } as EChartsOption;
  }
}
